import { FieldArray, FieldArrayRenderProps } from 'react-final-form-arrays';
import { v4 as uuidv4 } from 'uuid';
import { ARRAY_ERROR } from 'final-form';
import { FormSpy } from 'react-final-form';
import { connect } from 'react-redux';
import { useCallback } from 'react';

import { makeRequired } from 'CorePackage/MainModule/validators/isRequired';
import { EXPERTS_BLOCK_ID, getExpertBlockId } from 'KnmErknmPackage/MainModule/constants/knmErknmFieldsElementsIds';
import { IKnmErknmFormExpert } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormExpert';
import { IKnmFormRef } from 'KnmPackage/MainModule/interfaces/IKnmFormRef';
import { getIsRsnCngPPXXII, getKnmFormRef } from 'KnmPackage/MainModule/selectors/knmSelectors';
import { Button } from 'UIPackage/MainModule/components/Button';
import { CloseButton } from 'UIPackage/MainModule/components/CloseButton';
import { TextInputField } from 'UIPackage/MainModule/components/TextInputField';
import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { Row } from 'UIPackage/FormModule/components/Row';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { SelectInputFieldAutoOptions } from 'UIPackage/MainModule/components/SelectInputFieldAutoOptions';
import { CommonCatalogsNamesEnum } from 'CorePackage/CatalogsModule/enums/CommonCatalogsNamesEnum';
import { ErknmApproveRemarkSectionCodeEnum } from 'KnmErknmPackage/ErknmApproveRemarkModule/enums/ErknmApproveRemarkSectionCodeEnum';

import styles from './ErknmExperts.module.scss';
import { ErknmApproveRemark } from '../../ErknmApproveRemarkModule/components/ErknmApproveRemark';
import { ErknmApproveRemarkAddButton } from '../../ErknmApproveRemarkModule/components/ErknmApproveRemarkAddButton';
import { isExpertsEnabled } from '../../MainModule/fieldsAvailability/erknmExpertsFieldsAvailability';
import { ErknmApproveRemarkChanges } from '../../ErknmApproveRemarkModule/components/ErknmApproveRemarkChanges';
import { getTitleFieldWithRedStar } from '../../../functions/getTitleFieldWithRedStar';

interface IErknmExpertsProps {
  isPm?: boolean;
  isWatchOnly: boolean;
}

interface IErknmExpertsStoreProps {
  formRef: IKnmFormRef | undefined;
  isRsnCngPPXXII: boolean;
}

const ErknmExpertsWithoutStore = ({
  formRef,
  isPm,
  isWatchOnly,
  isRsnCngPPXXII,
}: IErknmExpertsProps & IErknmExpertsStoreProps) => {
  const isEnabled = isExpertsEnabled() && !isWatchOnly;

  const push = formRef?.current.mutators.push;
  const remove = formRef?.current.mutators.remove;

  const addExpert = useCallback(() => {
    if (isEnabled && push) {
      const addingExpert: IKnmErknmFormExpert = {
        title: undefined,
        type: undefined,
        listId: uuidv4(),
      };

      push('experts', addingExpert);
    }
  }, [push, isEnabled]);

  const makeRemoveExpertHandler = (index: number) => () => isEnabled && remove && remove('experts', index);

  return (
    <FieldBlock
      label={getTitleFieldWithRedStar({
        label: `Сведения об экспертах, экспертных организациях, специалистах, независимых органах инспекции, саморегулируемых
      организациях и иных лицах, привлекаемых для проведения ${isPm ? 'ПМ' : 'КНМ'}`,
        sectionName: 'experts',
        field: `experts`,
        isPm,
      })}
      id={EXPERTS_BLOCK_ID}
      button={
        <>
          <Button
            className="shared-btn-add"
            text="Добавить"
            variant="link"
            color="primary"
            type="button"
            disabled={!isEnabled || isRsnCngPPXXII}
            onClick={addExpert}
          />
          <ErknmApproveRemarkAddButton sectionCode={ErknmApproveRemarkSectionCodeEnum.EXPERTS} />
        </>
      }
    >
      <FormSpy subscription={{ errors: true, touched: true }}>
        {props => {
          const fieldError = props.errors?.experts?.[ARRAY_ERROR];
          const isFieldTouched = props.touched?.experts;

          if (isFieldTouched && fieldError) {
            return <p className="shared-input-error shared-row__small-margin">{fieldError}</p>;
          }

          return null;
        }}
      </FormSpy>
      <ErknmApproveRemark sectionCode={ErknmApproveRemarkSectionCodeEnum.EXPERTS} />
      <FieldArray name="experts">
        {({ fields }: FieldArrayRenderProps<IKnmErknmFormExpert, HTMLElement>) => {
          if (!fields.length) {
            return null;
          }

          return (
            <Row>
              {fields.map((expertsFieldName, sIndex) => {
                const fieldValue: IKnmErknmFormExpert = fields.value?.[sIndex];
                const id = getExpertBlockId(fieldValue.listId);

                return (
                  <Row key={id} margin="medium" isListItem id={id}>
                    <div className="shared-field-row">
                      <TextInputField
                        className={styles.TitleField}
                        id={`${expertsFieldName}.title`}
                        name={`${expertsFieldName}.title`}
                        placeholder={getTitleFieldWithRedStar({
                          label: 'Введите наименование',
                          field: `${expertsFieldName}.title`,
                          isPm,
                          isKnm: !isPm,
                        })}
                        disabled={!isEnabled}
                      />
                      <SelectInputFieldAutoOptions
                        className={styles.TypeField}
                        id={`${expertsFieldName}.type`}
                        name={`${expertsFieldName}.type`}
                        placeholder={getTitleFieldWithRedStar({
                          label: 'Выберите тип',
                          field: `${expertsFieldName}.type`,
                          isPm,
                          isKnm: !isPm,
                        })}
                        size="medium"
                        disabled={!isEnabled}
                        optionName={CommonCatalogsNamesEnum.ERKNM_KNM_EXPERT_TYPES}
                        validate={makeRequired('Поле не может быть пустым')}
                      />
                      <CloseButton className="shared-deleting-row-btn" onClick={makeRemoveExpertHandler(sIndex)} />
                      <div className={styles.RemarkChanges}>
                        <ErknmApproveRemarkChanges
                          sectionCode={ErknmApproveRemarkSectionCodeEnum.EXPERTS}
                          guid={fieldValue.guid}
                        />
                      </div>
                    </div>
                  </Row>
                );
              })}
            </Row>
          );
        }}
      </FieldArray>
    </FieldBlock>
  );
};

const mapStateToProps = (state: IRootState): IErknmExpertsStoreProps => ({
  formRef: getKnmFormRef(state),
  isRsnCngPPXXII: getIsRsnCngPPXXII(state),
});

export const ErknmExperts = connect(mapStateToProps)(ErknmExpertsWithoutStore);
